import {
  getDefaultAppState,
  clearAppStateForLocalStorage,
} from "@excalidraw/excalidraw/appState";

import type { AppState } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import { STORAGE_KEYS } from "../app_constants";
import { updateBrowserStateVersion } from "./tabSync";
import { importFromLocalStorage, saveSceneToLocalStorage } from "./localStorage";

export const DEFAULT_PROJECT = "Default";

export type BoardScene = {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
};

export type BoardRecord = {
  id: string;
  name: string;
  project: string;
  updatedAt: number;
  scene: BoardScene;
};

export type BoardCatalog = {
  activeBoardId: string;
  boards: BoardRecord[];
};

const BOARD_CATALOG_KEY = STORAGE_KEYS.LOCAL_STORAGE_BOARDS;

const createBoardId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `board-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeScene = (scene: BoardScene, name: string): BoardScene => {
  const appState = clearAppStateForLocalStorage(scene.appState);
  return {
    elements: [...scene.elements],
    appState: {
      ...appState,
      name,
    },
  };
};

const getUniqueBoardName = (
  baseName: string,
  boards: readonly BoardRecord[],
  excludeBoardId?: string,
) => {
  const trimmedBaseName = baseName.trim();
  const existingNames = new Set(
    boards
      .filter((board) => board.id !== excludeBoardId)
      .map((board) => board.name),
  );

  if (!existingNames.has(trimmedBaseName)) {
    return trimmedBaseName;
  }

  let suffix = 2;
  let nextName = `${trimmedBaseName} ${suffix}`;
  while (existingNames.has(nextName)) {
    suffix += 1;
    nextName = `${trimmedBaseName} ${suffix}`;
  }
  return nextName;
};

const getUniqueProjectName = (
  baseName: string,
  existingProjects: readonly string[],
) => {
  const trimmed = baseName.trim();
  if (!existingProjects.includes(trimmed)) {
    return trimmed;
  }

  let suffix = 2;
  let next = `${trimmed} ${suffix}`;
  while (existingProjects.includes(next)) {
    suffix += 1;
    next = `${trimmed} ${suffix}`;
  }
  return next;
};

const makeDefaultBoardName = (boardCount: number) => {
  return `Board ${boardCount + 1}`;
};

const getSceneFromLocalStorage = (): BoardScene => {
  const { elements, appState } = importFromLocalStorage();
  return {
    elements,
    appState: appState ?? getDefaultAppState(),
  };
};

/** Migrate legacy boards that lack the `project` field. */
const migrateCatalog = (catalog: BoardCatalog): BoardCatalog => {
  const needsMigration = catalog.boards.some(
    (b) => (b as BoardRecord & { project?: string }).project === undefined,
  );
  if (!needsMigration) {
    return catalog;
  }
  return {
    ...catalog,
    boards: catalog.boards.map((b) => ({
      ...b,
      project: (b as BoardRecord & { project?: string }).project ?? DEFAULT_PROJECT,
    })),
  };
};

export const loadBoardCatalog = (): BoardCatalog | null => {
  try {
    const data = localStorage.getItem(BOARD_CATALOG_KEY);
    if (!data) {
      return null;
    }

    const catalog = JSON.parse(data) as BoardCatalog;
    if (!catalog?.boards?.length || !catalog.activeBoardId) {
      return null;
    }

    return migrateCatalog(catalog);
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const saveBoardCatalog = (catalog: BoardCatalog) => {
  try {
    localStorage.setItem(BOARD_CATALOG_KEY, JSON.stringify(catalog));
    updateBrowserStateVersion(STORAGE_KEYS.VERSION_BOARDS);
  } catch (error) {
    console.error(error);
  }
};

export const loadOrCreateBoardCatalog = () => {
  const catalog = loadBoardCatalog();
  if (catalog) {
    return catalog;
  }

  const scene = getSceneFromLocalStorage();
  const boardName = scene.appState.name?.trim() || makeDefaultBoardName(0);
  const boardId = createBoardId();

  const nextCatalog: BoardCatalog = {
    activeBoardId: boardId,
    boards: [
      {
        id: boardId,
        name: boardName,
        project: DEFAULT_PROJECT,
        updatedAt: Date.now(),
        scene: normalizeScene(scene, boardName),
      },
    ],
  };

  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const getBoardById = (catalog: BoardCatalog, boardId: string) => {
  return catalog.boards.find((board) => board.id === boardId) || null;
};

/** Returns sorted unique project names from the catalog. */
export const getProjects = (catalog: BoardCatalog): string[] => {
  const names = new Set(catalog.boards.map((b) => b.project));
  return Array.from(names).sort((a, b) => {
    if (a === DEFAULT_PROJECT) return -1;
    if (b === DEFAULT_PROJECT) return 1;
    return a.localeCompare(b);
  });
};

export const saveSceneToBoard = (
  catalog: BoardCatalog,
  boardId: string,
  scene: BoardScene,
  opts?: { syncCurrentStorage?: boolean },
) => {
  const board = getBoardById(catalog, boardId);
  if (!board) {
    return catalog;
  }

  const nextBoard = {
    ...board,
    updatedAt: Date.now(),
    scene: normalizeScene(scene, board.name),
  };

  const nextCatalog: BoardCatalog = {
    ...catalog,
    boards: catalog.boards.map((item) =>
      item.id === boardId ? nextBoard : item,
    ),
  };

  if (opts?.syncCurrentStorage && catalog.activeBoardId === boardId) {
    saveSceneToLocalStorage(nextBoard.scene.elements, nextBoard.scene.appState);
  }

  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const activateBoard = (catalog: BoardCatalog, boardId: string) => {
  const board = getBoardById(catalog, boardId);
  if (!board) {
    return catalog;
  }

  const nextCatalog: BoardCatalog = {
    ...catalog,
    activeBoardId: boardId,
  };

  saveSceneToLocalStorage(board.scene.elements, board.scene.appState);
  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const createBoard = (
  catalog: BoardCatalog,
  scene: BoardScene,
  name?: string,
  project: string = DEFAULT_PROJECT,
) => {
  const boardName = getUniqueBoardName(
    name?.trim() || makeDefaultBoardName(catalog.boards.length),
    catalog.boards,
  );
  const record: BoardRecord = {
    id: createBoardId(),
    name: boardName,
    project,
    updatedAt: Date.now(),
    scene: normalizeScene(scene, boardName),
  };
  const nextCatalog: BoardCatalog = {
    activeBoardId: record.id,
    boards: [record, ...catalog.boards],
  };

  saveSceneToLocalStorage(record.scene.elements, record.scene.appState);
  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const duplicateBoard = (catalog: BoardCatalog, boardId: string) => {
  const board = getBoardById(catalog, boardId);
  if (!board) {
    return catalog;
  }

  const boardName = getUniqueBoardName(`Copy of ${board.name}`, catalog.boards);
  const record: BoardRecord = {
    id: createBoardId(),
    name: boardName,
    project: board.project,
    updatedAt: Date.now(),
    scene: normalizeScene(board.scene, boardName),
  };
  const nextCatalog: BoardCatalog = {
    activeBoardId: record.id,
    boards: [record, ...catalog.boards],
  };

  saveSceneToLocalStorage(record.scene.elements, record.scene.appState);
  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const renameBoard = (
  catalog: BoardCatalog,
  boardId: string,
  newName: string,
) => {
  const board = getBoardById(catalog, boardId);
  if (!board) {
    return catalog;
  }

  const uniqueName = getUniqueBoardName(newName, catalog.boards, boardId);
  const nextCatalog: BoardCatalog = {
    ...catalog,
    boards: catalog.boards.map((item) =>
      item.id === boardId
        ? {
            ...item,
            name: uniqueName,
            updatedAt: Date.now(),
            scene: normalizeScene(item.scene, uniqueName),
          }
        : item,
    ),
  };

  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const moveBoardToProject = (
  catalog: BoardCatalog,
  boardId: string,
  projectName: string,
) => {
  const nextCatalog: BoardCatalog = {
    ...catalog,
    boards: catalog.boards.map((item) =>
      item.id === boardId
        ? { ...item, project: projectName, updatedAt: Date.now() }
        : item,
    ),
  };

  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const createProject = (
  catalog: BoardCatalog,
  projectName: string,
): { catalog: BoardCatalog; projectName: string } => {
  const existingProjects = getProjects(catalog);
  const uniqueName = getUniqueProjectName(projectName, existingProjects);
  return { catalog, projectName: uniqueName };
};

export const renameProject = (
  catalog: BoardCatalog,
  oldName: string,
  newName: string,
): BoardCatalog => {
  const existingProjects = getProjects(catalog).filter((p) => p !== oldName);
  const uniqueName = getUniqueProjectName(newName, existingProjects);
  const nextCatalog: BoardCatalog = {
    ...catalog,
    boards: catalog.boards.map((item) =>
      item.project === oldName ? { ...item, project: uniqueName } : item,
    ),
  };

  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};

export const deleteProject = (
  catalog: BoardCatalog,
  projectName: string,
): BoardCatalog => {
  const boardIds = catalog.boards
    .filter((b) => b.project === projectName)
    .map((b) => b.id);

  // Reuse deleteBoard for each board so active-board switching + last-board
  // guard are handled automatically.
  let next = catalog;
  for (const id of boardIds) {
    next = deleteBoard(next, id);
  }
  return next;
};

export const deleteBoard = (catalog: BoardCatalog, boardId: string) => {
  const remainingBoards = catalog.boards.filter((board) => board.id !== boardId);
  if (!remainingBoards.length) {
    return createBoard(
      { activeBoardId: boardId, boards: [] },
      { elements: [], appState: getDefaultAppState() },
      makeDefaultBoardName(0),
    );
  }

  const nextActiveBoardId =
    catalog.activeBoardId === boardId
      ? remainingBoards[0].id
      : catalog.activeBoardId;

  const nextCatalog: BoardCatalog = {
    activeBoardId: nextActiveBoardId,
    boards: remainingBoards,
  };

  const activeBoard =
    remainingBoards.find((board) => board.id === nextActiveBoardId) ||
    remainingBoards[0];

  if (catalog.activeBoardId === boardId) {
    saveSceneToLocalStorage(
      activeBoard.scene.elements,
      activeBoard.scene.appState,
    );
  }

  saveBoardCatalog(nextCatalog);
  return nextCatalog;
};
